import { UseFormSetError, FieldError, UseFormClearErrors } from 'react-hook-form';
import { CombinedError } from 'urql';
import { GraphqlErrorResponse } from './find-graphql-error';
import { Text, TextProps } from '@chakra-ui/react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { BACKEND_URLS } from '@/config/urls';

enum ApplicationError {
  NETWORK_ERROR = 'network-error',
  UNAUTHORIZED = 'Unauthorized', // Lowercased as it is returned from Auth0
  GAME_NOT_FOUND = 'GAME_NOT_FOUND',
  ALREADY_REVIEWED = 'ALREADY_REVIEWED',
  REVIEW_NOT_FOUND = 'REVIEW_NOT_FOUND',
  REPLY_NOT_FOUND = 'REPLY_NOT_FOUND',
}

const GRAPHQL_API_ERROR = '[GraphQL]';
const GRAPHQL_NETWORK_ERROR = '[Network]';

type UnknownFieldsType = Record<string, unknown>;

type UnknownFieldsError = Record<string, FieldError>;

export type FormControlError<Fields extends UnknownFieldsType> = {
  field: keyof Fields;
  value: string;
};

export type FormControlErrorMap<Fields extends UnknownFieldsType> = {
  [key: string]: FormControlError<Fields>;
};

/**
 * Returns an errorMap value if its key matches (not exact) the provided errorMessage
 * @param errorMap
 * @param errorMessage
 * @returns FormControlError
 */
export const findFormControlError = <Fields extends UnknownFieldsType>(
  errorMap: FormControlErrorMap<Fields>,
  errorMessage: string,
): FormControlError<Fields> | null =>
  Object.entries(errorMap).reduce<FormControlError<Fields> | null>(
    (found, [key, error]) => found || (errorMessage.includes(key) ? error : found),
    null,
  );

/**
 * Attempts to map errors messages in a common GraphQL error response to the values in the provided errorMap.
 * Both the generic error message, and messages provided in a BAD_USER_INPUT error, will be matched
 * @param errors
 * @param errorMap
 * @returns FormControlError[]
 */
export const mapResponseErrorToErrorMessages = <Fields extends UnknownFieldsType>(
  errors: CombinedError,
  errorMap: FormControlErrorMap<Fields>,
): FormControlError<Fields>[] => {
  const { message: genericErrorMessage } = errors;

  /**
   * Collect the `response` property value from a common GraphqlError response. An example of the response is added below.
   *
   * @example
   * const combinedError = {
   *   message: 'Bad request',
   *   graphQLErrors: [
   *     {
   *       message: 'Bad Request Exception',
   *       extensions: {
   *         code: 'BAD_USER_INPUT',
   *         response: { statusCode: 400, message: ['INPUT_ERROR_CODE'], error: 'Bad Request' },
   *       },
   *     },
   *   ],
   * }
   */
  const inputErrorResponse = errors.graphQLErrors?.find(
    error => (error.extensions?.code as string) === 'BAD_USER_INPUT',
  )?.extensions?.response as GraphqlErrorResponse | undefined;

  // Collects the messages in the response, and ensures an array results from it
  const inputErrorMessages = inputErrorResponse
    ? Array.isArray(inputErrorResponse.message)
      ? inputErrorResponse.message
      : [inputErrorResponse.message]
    : [];

  const errorMessages = [genericErrorMessage, ...inputErrorMessages];

  // Maps each found message to a value from the errorMap provided to the util
  const toFormControlErrors = errorMessages.reduce((all, message) => {
    const found = findFormControlError(errorMap, message);
    if (!found) return all;

    return [...all, found];
  }, []);

  return toFormControlErrors;
};

const UNCAUGHT_FORM_ERROR = 'uncaughtFormError';

/**
 * Sets errors resolved from an error map to the form, and puts focus on the input where the first error occurs.
 * If errors occured but none could be resolved to the provided errorMap, an uncaught error is set
 * which can be rendered with `<UncaughFormControlError />`.
 * @param error
 * @param errorMap
 * @param setErrorFn
 * @returns void
 */
export function setFormControlErrors(
  error: CombinedError,
  errorMap: FormControlErrorMap<UnknownFieldsType>,
  setErrorFn: UseFormSetError<UnknownFieldsType>,
  clearErrorsFn: UseFormClearErrors<UnknownFieldsType>,
) {
  /**
   * As the custom 'uncaughtError' we set is not associated with an input field we need to explicitly
   * clear all errors to prevent the error from being persisted. This is documented as a rule in react-hook-form's
   * documentation.
   * @see https://react-hook-form.com/api/useform/seterror
   */
  clearErrorsFn();

  const errorMessages = mapResponseErrorToErrorMessages(error, errorMap);

  if (!errorMessages.length) {
    setErrorFn(UNCAUGHT_FORM_ERROR, {
      type: 'uncaughtError',
      message: error.message,
    });
    return;
  }

  errorMessages.forEach((message, i) =>
    setErrorFn(message.field, { type: 'inputError', message: message.value }, { shouldFocus: !i }),
  );
}

/**
 * Renders the 'UncaughtError' set when an error occurs which cannot be mapped to a predefined errorMap
 * @param errors
 * @returns Text | null
 */
export const UncaughtFormControlError: React.FC<{ errors: UnknownFieldsError } & TextProps> = ({
  errors,
  ...textProps
}) => {
  const { t } = useTranslation('errors');
  const router = useRouter();

  const formatGraphQLMessage = (message: string) => {
    if (!message) return '';
    if (message.includes(GRAPHQL_API_ERROR)) {
      return message.split(' ')[1];
    }
    if (message.includes(GRAPHQL_NETWORK_ERROR)) {
      return ApplicationError.NETWORK_ERROR;
    }
  };

  const formattedMessage = formatGraphQLMessage(errors[UNCAUGHT_FORM_ERROR]?.message);

  if (formattedMessage === ApplicationError.UNAUTHORIZED) {
    router.push(`${BACKEND_URLS.LOGOUT}?last_visited=${router.asPath}`);
  }

  return (
    <>
      {formattedMessage ? (
        <Text textStyle="form-error-message" data-testid="uncaught-error-message" {...textProps}>
          {t(`apiErrors.${formattedMessage}`)}
        </Text>
      ) : null}
    </>
  );
};
